import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateBreadcrumbSchema } from '../../utils/seo';

const TIMELINE_STEPS = [
  {
    step: '01',
    icon: 'ri-id-card-line',
    title: "Get Nevada Driver's License",
    deadline: 'Within 30 Days',
    deadlineColor: 'bg-red-600',
    desc: "New residents must obtain a Nevada driver's license within 30 days of establishing residency.",
    requirements: [
      'Proof of identity (birth certificate, passport)',
      'Social Security Number or proof of legal presence',
      '2 Nevada residency documents',
      'Pass vision exam and written test if required',
    ],
  },
  {
    step: '02',
    icon: 'ri-car-line',
    title: 'Register Your Vehicle',
    deadline: 'Within 30 Days',
    deadlineColor: 'bg-red-600',
    desc: 'All vehicles must be registered in Nevada within 30 days of establishing residency.',
    requirements: [
      'Nevada driver\'s license',
      'Out-of-state title or registration',
      'Nevada auto insurance',
      'VIN verification and smog inspection',
    ],
  },
  {
    step: '03',
    icon: 'ri-government-line',
    title: 'Update Voter Registration',
    deadline: 'Optional',
    deadlineColor: 'bg-green-600',
    desc: 'Register to vote in Nevada to participate in local and state elections.',
    requirements: [
      'Nevada driver\'s license or ID',
      'Nevada address',
      'Complete voter registration form',
    ],
  },
];

const RESIDENCY_DOCS = [
  {
    category: 'Primary Documents (2 Required)',
    icon: 'ri-file-list-3-line',
    color: 'bg-[#003366]',
    docs: [
      'Nevada employment pay stub',
      'Nevada bank statement',
      'Nevada utility bill (gas, electric, water)',
      'Nevada property tax bill',
      'Nevada vehicle registration',
      'Nevada voter registration card',
    ],
  },
  {
    category: 'Secondary Documents',
    icon: 'ri-file-text-line',
    color: 'bg-green-700',
    docs: [
      'Lease agreement or mortgage statement',
      'Nevada medical/dental records',
      'Nevada school enrollment records',
      'Nevada insurance policies',
      'IRS tax return showing Nevada address',
      'Nevada business license',
    ],
  },
];

const BENEFITS = [
  { icon: 'ri-user-heart-line', title: 'Personal Guidance', desc: 'One-on-one guidance through every step of the Nevada residency process.' },
  { icon: 'ri-time-line', title: 'Save Time', desc: 'Skip the long DMV lines. We handle everything efficiently in one visit.' },
  { icon: 'ri-shield-check-line', title: 'Ensure Compliance', desc: 'Avoid penalties. We ensure all requirements are met correctly and on time.' },
  { icon: 'ri-file-list-3-line', title: 'Document Checklist', desc: 'We provide a personalized checklist so you arrive prepared and ready.' },
];

export default function NewResidentPage() {
  const [openStep, setOpenStep] = useState<number | null>(0);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  useSEO({
    title: 'New Nevada Residents DMV Guide Las Vegas - Vehicle Registration & License',
    description: "Complete guide for new Nevada residents on DMV requirements. Register your vehicle, get a Nevada driver's license, and complete residency within 30 days. Speedy DMV Services Las Vegas helps you comply fast.",
    keywords: 'new Nevada resident DMV, Nevada vehicle registration new resident, Las Vegas DMV guide, move to Nevada vehicle registration',
    canonical: `${siteUrl}/new-resident`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        generateBreadcrumbSchema([{ name: 'Home', url: '/' }, { name: 'New Residents', url: '/new-resident' }])
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white font-sans">
      <Header />

      {/* HERO */}
      <section className="relative pt-[104px]">
        <div className="relative h-[400px] md:h-[460px] overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/004c39275810fc6f09a4b94decbf8640.jpg"
            alt="New Nevada Residents Guide"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#003366]/80"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <div className="flex items-center space-x-2 text-white/60 text-xs mb-5">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#F5A623]">New Residents</span>
            </div>
            <div className="inline-flex items-center space-x-2 bg-[#F5A623] text-white text-xs font-bold px-4 py-1.5 rounded mb-4 uppercase tracking-widest">
              <i className="ri-map-pin-line"></i>
              <span>Welcome to Nevada — Las Vegas DMV Services</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4 max-w-3xl">
              New Nevada <span className="text-[#F5A623]">Residents Guide</span>
            </h1>
            <p className="text-white/85 text-lg max-w-2xl mb-7">
              Everything you need to establish Nevada residency — driver's license, vehicle registration, and more. We make the transition easy.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="tel:7029797202" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-7 py-3.5 font-bold text-sm transition-colors whitespace-nowrap rounded">
                <i className="fas fa-phone text-xs"></i>
                <span>Call Now: 702.979.7202</span>
              </a>
              <Link to="/appointment" className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-100 text-[#003366] px-7 py-3.5 font-bold text-sm transition-colors whitespace-nowrap rounded">
                <i className="ri-calendar-check-line"></i>
                <span>Book Appointment</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* NOTICE BAR */}
      <div className="bg-[#FFF8E7] border-b-2 border-[#F5A623]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center space-x-3">
            <i className="ri-information-line text-[#F5A623] text-lg flex-shrink-0"></i>
            <p className="text-sm text-[#7a5c00]">
              <strong>We are NOT the Nevada DMV.</strong> Speedy DMV Services is a licensed, independent 3rd party provider. We assist with DMV transactions for a service fee. Official DMV: <a href="https://dmv.nv.gov" target="_blank" rel="noopener noreferrer nofollow" className="underline font-semibold">dmv.nv.gov</a>
            </p>
          </div>
        </div>
      </div>

      {/* QUICK STATS */}
      <div className="bg-[#003366]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#1a4480]">
            {[
              { value: '30 Days', label: 'Residency Deadline' },
              { value: 'Same Day', label: 'Service Available' },
              { value: '1 Visit', label: 'Complete Package' },
              { value: 'All 50 States', label: 'Transfers Accepted' },
            ].map((s, i) => (
              <div key={i} className="py-4 px-6 text-center">
                <p className="text-[#F5A623] text-lg font-bold">{s.value}</p>
                <p className="text-white/60 text-xs mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* DEADLINE WARNING */}
      <section className="py-8 bg-red-50 border-b border-red-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 flex items-center justify-center bg-red-600 rounded flex-shrink-0">
              <i className="ri-alarm-warning-line text-white text-xl"></i>
            </div>
            <div>
              <h3 className="font-bold text-red-800 text-base mb-1">Important Deadlines — Don't Miss Them!</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-2">
                {[
                  { label: '30 Days', desc: "Get Nevada driver's license after establishing residency" },
                  { label: '30 Days', desc: 'Register all vehicles in Nevada' },
                  { label: 'Penalties Apply', desc: 'Late registration and licensing fees increase over time' },
                  { label: 'Required', desc: 'Nevada auto insurance before vehicle registration' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-2">
                    <span className="text-red-600 font-bold text-sm whitespace-nowrap">{item.label}:</span>
                    <span className="text-red-700 text-sm">{item.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TIMELINE ACCORDION */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Your New Resident Timeline</h2>
          <p className="text-gray-500 mb-8 pl-5">Follow these steps to complete your Nevada residency requirements.</p>
          <div className="space-y-4">
            {TIMELINE_STEPS.map((t, i) => (
              <div
                key={i}
                className={`border rounded-lg overflow-hidden transition-all cursor-pointer ${openStep === i ? 'border-[#003366] shadow-md' : 'border-gray-200 hover:border-[#003366]/40'}`}
                onClick={() => setOpenStep(openStep === i ? null : i)}
              >
                <div className={`flex items-center justify-between px-5 py-4 ${openStep === i ? 'bg-[#003366]' : 'bg-white hover:bg-gray-50'} transition-colors`}>
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 flex items-center justify-center rounded flex-shrink-0 ${openStep === i ? 'bg-[#F5A623]' : 'bg-[#003366]'}`}>
                      <i className={`${t.icon} ${openStep === i ? 'text-white' : 'text-[#F5A623]'} text-base`}></i>
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className={`text-xs font-bold ${openStep === i ? 'text-[#F5A623]' : 'text-[#F5A623]'}`}>Step {t.step}</span>
                        <p className={`font-bold text-sm ${openStep === i ? 'text-white' : 'text-[#003366]'}`}>{t.title}</p>
                        <span className={`text-xs px-2 py-0.5 rounded font-semibold ${t.deadlineColor} text-white`}>{t.deadline}</span>
                      </div>
                      <p className={`text-xs ${openStep === i ? 'text-white/70' : 'text-gray-400'}`}>{t.desc}</p>
                    </div>
                  </div>
                  <i className={`${openStep === i ? 'ri-arrow-up-s-line text-white' : 'ri-arrow-down-s-line text-[#003366]'} text-xl flex-shrink-0`}></i>
                </div>
                {openStep === i && (
                  <div className="px-5 py-4 bg-white border-t border-gray-100">
                    <p className="text-xs font-bold text-[#003366] uppercase tracking-widest mb-3">Requirements</p>
                    <ul className="space-y-2">
                      {t.requirements.map((req, j) => (
                        <li key={j} className="flex items-center space-x-2 text-sm text-gray-700">
                          <i className="ri-checkbox-circle-fill text-[#F5A623] flex-shrink-0"></i>
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <a href="tel:7029797202" className="inline-flex items-center space-x-2 bg-[#003366] hover:bg-[#002244] text-white px-5 py-2.5 rounded font-bold text-sm transition-colors whitespace-nowrap">
                        <i className="fas fa-phone text-xs"></i>
                        <span>Call for Help with This Step</span>
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RESIDENCY DOCUMENTS */}
      <section className="py-14 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Nevada Residency Documents</h2>
          <p className="text-gray-500 mb-8 pl-5">You'll need 2 primary documents to prove Nevada residency.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {RESIDENCY_DOCS.map((cat, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className={`${cat.color} px-5 py-3 flex items-center space-x-2`}>
                  <i className={`${cat.icon} text-white text-base`}></i>
                  <span className="text-white font-bold text-sm">{cat.category}</span>
                </div>
                <ul className="p-5 space-y-2.5">
                  {cat.docs.map((doc, j) => (
                    <li key={j} className="flex items-start space-x-2 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-fill text-[#F5A623] flex-shrink-0 mt-0.5"></i>
                      <span>{doc}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-6 bg-[#FFF8E7] border-l-4 border-[#F5A623] rounded-r-lg p-5">
            <div className="flex items-start space-x-3">
              <i className="ri-alarm-warning-line text-[#F5A623] text-lg flex-shrink-0 mt-0.5"></i>
              <div>
                <p className="font-bold text-[#7a5c00] text-sm mb-1">Document Rules</p>
                <ul className="text-sm text-[#7a5c00] space-y-1">
                  <li>• Documents must be dated within 30 days of application</li>
                  <li>• All documents must show your Nevada address</li>
                  <li>• Documents must be originals or certified copies</li>
                  <li>• PO Box addresses are NOT acceptable for residency proof</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-14 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Why Choose Our New Resident Services</h2>
          <p className="text-gray-500 mb-10 pl-5">We make your Nevada transition as smooth and stress-free as possible.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {BENEFITS.map((b, i) => (
              <div key={i} className="group flex flex-col p-5 border border-gray-200 rounded-lg hover:border-[#003366] hover:bg-blue-50/30 transition-all">
                <div className="w-11 h-11 flex items-center justify-center bg-[#003366] rounded mb-4 group-hover:bg-[#F5A623] transition-colors flex-shrink-0">
                  <i className={`${b.icon} text-[#F5A623] group-hover:text-white text-base transition-colors`}></i>
                </div>
                <h3 className="font-bold text-[#003366] text-sm mb-1">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PACKAGE */}
      <section className="py-14 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Complete New Resident Package</h2>
          <p className="text-gray-500 mb-8 pl-5">Everything you need to establish Nevada residency — handled in one visit.</p>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-[#003366] px-6 py-4">
              <h3 className="text-white font-bold text-base flex items-center space-x-2">
                <i className="ri-gift-line text-[#F5A623]"></i>
                <span>What's Included</span>
              </h3>
            </div>
            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs font-bold text-[#003366] uppercase tracking-widest mb-3">Package Includes</p>
                  <ul className="space-y-2.5">
                    {["Driver's license guidance and paperwork", 'Vehicle registration processing', 'Title transfer assistance', 'Nevada license plates', 'Document notarization'].map((item, i) => (
                      <li key={i} className="flex items-center space-x-2 text-sm text-gray-700">
                        <i className="ri-checkbox-circle-fill text-[#F5A623] flex-shrink-0"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-bold text-[#003366] uppercase tracking-widest mb-3">Additional Benefits</p>
                  <ul className="space-y-2.5">
                    {['Expert consultation on requirements', 'Same-day processing when possible', 'No DMV line waiting', 'Follow-up support if needed', 'Appointment scheduling convenience'].map((item, i) => (
                      <li key={i} className="flex items-center space-x-2 text-sm text-gray-700">
                        <i className="ri-checkbox-circle-fill text-[#F5A623] flex-shrink-0"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-[#003366]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">
            Welcome to Nevada — <span className="text-[#F5A623]">Let's Get You Set Up!</span>
          </h2>
          <p className="text-white/75 mb-8 leading-relaxed">
            Don't wait until the 30-day deadline. Call us today and we'll handle everything in one convenient visit.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <a href="tel:7029797202" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-8 py-4 font-bold text-base transition-colors whitespace-nowrap rounded">
              <i className="fas fa-phone text-sm"></i>
              <span>Call 702.979.7202</span>
            </a>
            <Link to="/appointment" className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-100 text-[#003366] px-8 py-4 font-bold text-base transition-colors whitespace-nowrap rounded">
              <i className="ri-calendar-check-line"></i>
              <span>Book Appointment</span>
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-5">
            {['Licensed 3rd Party Provider', 'Same-Day Processing', 'Complete Package', 'No DMV Lines'].map(b => (
              <div key={b} className="flex items-center space-x-1.5 text-white/60 text-xs">
                <i className="ri-checkbox-circle-fill text-[#F5A623]"></i>
                <span>{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
